import React, { useState, useRef, useCallback, useEffect, useContext } from 'react';
import { useScrollSnap } from '../hooks/useScrollSnap';
import {
  IonPage,
  IonContent,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonList,
  IonItem,
  IonLabel,
  IonToggle,
  IonIcon,
  IonButton,
  IonGrid,
  IonSegment,
  IonSegmentButton,
  IonAlert,
  IonToast,
  IonBadge,
  IonModal,
  useIonRouter,
} from '@ionic/react';
import {
  chevronBack,
  closeOutline,
  chevronForwardOutline,
  contrastOutline,
  sunnyOutline,
  moonOutline,
  desktopOutline,
  notificationsOutline,
  mailOutline,
  volumeHighOutline,
  phonePortraitOutline,
  lockClosedOutline,
  keyOutline,
  fingerPrintOutline,
  shieldCheckmarkOutline,
  gridOutline,
  trashBinOutline,
  informationCircleOutline,
} from 'ionicons/icons';
import { useAuth } from '../contexts';
import { AuthContext } from '../contexts/auth-context-core';
import { rsAuth } from '@rsmaids/shared/services/rsAuthClient';
import { DataServiceFactory } from '@rsmaids/shared/services/DataServiceFactory';
import { requestNotificationPermission } from '../utils/notificationUtils';
import BasePageWithHeader, { TitleCard } from '../components/BasePageWithHeader';
import LanguageSetting from '../components/LanguageSetting';
import { ShortcutsEditModal } from '../components/ShortcutsEditModal';
import { PasskeyManagement } from '../components/PasskeyManagement';
import { catalogueForRole } from './dashboard/shortcutCatalogues';
import { Security } from './Security/Security';
import NotificationSettings from './NotificationSettings';
import styles from './AppSettings.module.scss';
import { useTranslation } from 'react-i18next';

interface AppSettingsProps {
  onClose?: () => void;
}

const APP_VERSION =import.meta.env.VITE_APP_VERSION || '';

const AppSettings: React.FC<AppSettingsProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const router = useIonRouter();
  const { authState } = useAuth();
  const { user } = authState;
  const authContext = useContext(AuthContext);

  const handleClose = useCallback(() => {
    if (onClose) onClose();
    else if (router.canGoBack()) router.goBack();
    else router.push('/tabs/dashboard');
  }, [onClose, router]);

  // ── Scroll / header animation ─────────────────────────────────────────
  const baseHeaderRef = useRef<HTMLIonHeaderElement>(null);
  const titleCardAnimationRef = useRef<HTMLIonCardElement>(null);
  const containerRef = useRef<HTMLIonGridElement>(null);

  const applyProgress = useCallback((progress: number, _smooth: boolean) => {
    baseHeaderRef.current?.style.setProperty('--title-opacity', String(progress));
    if (titleCardAnimationRef.current) {
      titleCardAnimationRef.current.style.setProperty('--content-opacity', String(1 - progress));
      titleCardAnimationRef.current.style.setProperty('--content-scale', `scale(${1 - progress * 0.2})`);
    }
  }, []);

  const handleScroll = useScrollSnap(containerRef, 66, applyProgress);

  // ── Theme ────────────────────────────────────────────────────────────
  const [currentTheme, setCurrentTheme] = useState(() => localStorage.getItem('theme') || 'system');
  const applyTheme = useCallback((theme: string) => {
    localStorage.setItem('theme', theme);
    setCurrentTheme(theme);
    window.applyTheme?.(theme);
  }, []);

  // ── Notification preferences (master toggles; full page lives elsewhere) ──
  const notifPrefs = user?.preferences?.notifications;
  const [pushEnabled, setPushEnabled] = useState(notifPrefs?.push ?? true);
  const [emailEnabled, setEmailEnabled] = useState(notifPrefs?.email ?? true);
  const [soundEnabled, setSoundEnabled] = useState(notifPrefs?.sound ?? true);

  // Sync local toggle state from user.preferences.notifications whenever it
  // changes — computed during render (React's "adjust state when a prop
  // changes" pattern) rather than as setState calls inside an effect.
  const [prevNotifPrefsSource, setPrevNotifPrefsSource] = useState(user?.preferences?.notifications);
  if (prevNotifPrefsSource !== user?.preferences?.notifications) {
    setPrevNotifPrefsSource(user?.preferences?.notifications);
    const p = user?.preferences?.notifications;
    if (p) {
      if (p.push !== undefined) setPushEnabled(p.push);
      if (p.email !== undefined) setEmailEnabled(p.email);
      if (p.sound !== undefined) setSoundEnabled(p.sound);
    }
  }

  const savePrefs = useCallback(async (patch: Record<string, unknown>) => {
    try {
      await (await import('@rsmaids/shared/services/DataServiceFactory')).DataServiceFactory
        .getService().auth.updateUserPreferences({ notifications: patch } as never);
    } catch (err) {
      console.error('[AppSettings] Failed to save prefs:', err);
    }
  }, []);

  // Initial value already reads Notification.permission via the lazy
  // initializer below, so a mount-time effect re-checking the same value
  // would always be redundant (browser permission dialogs are triggered
  // by handleRequestPermission below, which sets this directly on response).
  const [browserPermission, setBrowserPermission] = useState<NotificationPermission>(
    'Notification' in window ? Notification.permission : 'denied',
  );
  const handleRequestPermission = useCallback(async () => {
    if (!('Notification' in window)) return;
    const result = await requestNotificationPermission({ prompt: true });
    setBrowserPermission(result);
  }, []);

  // ── Security ─────────────────────────────────────────────────────────
  const [passkeyCount, setPasskeyCount] = useState<number | null>(null);
  useEffect(() => {
    let active = true;
    rsAuth.listUserPasskeys()
      .then((r) => { if (active && r.success && r.passkeys) setPasskeyCount(r.passkeys.length); })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  const [showChangePassword, setShowChangePassword] = useState(false);
  const [toast, setToast] = useState('');

  const handleChangePassword = useCallback(async (data: {
    currentPassword?: string;
    newPassword?: string;
    confirmPassword?: string;
  }) => {
    const currentPassword = (data.currentPassword || '').trim();
    const newPassword = (data.newPassword || '').trim();
    const confirmPassword = (data.confirmPassword || '').trim();

    if (!currentPassword || !newPassword || !confirmPassword) {
      setToast(t('pages.AppSettings.pleaseFillInAllPasswordFields'));
      return;
    }
    if (newPassword.length < 6) {
      setToast(t('pages.AppSettings.newPasswordMustBeAtLeast'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setToast(t('pages.AppSettings.newPasswordsDoNotMatch'));
      return;
    }
    if (newPassword === currentPassword) {
      setToast(t('pages.AppSettings.newPasswordMustBeDifferentFrom'));
      return;
    }

    try {
      const result = await rsAuth.changePassword(currentPassword, newPassword);
      setToast(result.success ? t('pages.AppSettings.passwordChangedSuccessfully') : (result.error || t('pages.AppSettings.failedToChangePassword')));
    } catch (err) {
      console.error('[AppSettings] Change password error:', err);
      setToast(t('pages.AppSettings.anErrorOccurredWhileChangingYour'));
    }
  }, [t]);

  const [showSecurityModal, setShowSecurityModal] = useState(false);
  const [showPasskeysModal, setShowPasskeysModal] = useState(false);
  const [showNotificationsModal, setShowNotificationsModal] = useState(false);

  // ── Dashboard shortcuts ──────────────────────────────────────────────
  // Catalogue for the dashboard this user actually sees; null for roles with
  // no shortcut-editing dashboard (manager, client).
  const shortcutCatalogue = catalogueForRole(user?.role);
  const [showShortcutsModal, setShowShortcutsModal] = useState(false);
  const [activeShortcutIds, setActiveShortcutIds] = useState<string[]>(
    user?.preferences?.dashboardShortcuts || shortcutCatalogue?.defaultIds || [],
  );
  // Sync from user.preferences.dashboardShortcuts whenever it changes —
  // computed during render (React's "adjust state when a prop changes"
  // pattern) rather than as a setState call inside an effect.
  const [prevDashboardShortcuts, setPrevDashboardShortcuts] = useState(user?.preferences?.dashboardShortcuts);
  if (prevDashboardShortcuts !== user?.preferences?.dashboardShortcuts) {
    setPrevDashboardShortcuts(user?.preferences?.dashboardShortcuts);
    if (user?.preferences?.dashboardShortcuts) {
      setActiveShortcutIds(user.preferences.dashboardShortcuts);
    }
  }

  const saveShortcuts = useCallback(async (shortcuts: string[]) => {
    setActiveShortcutIds(shortcuts);
    const lsKey = shortcutCatalogue?.localStorageKey ?? 'dashboard-owner-shortcuts';
    try {
      await DataServiceFactory.getService().auth.updateUserPreferences({ dashboardShortcuts: shortcuts });
      authContext?.actions?.updateLocalPreferences({ dashboardShortcuts: shortcuts });
      localStorage.setItem(lsKey, JSON.stringify(shortcuts));
    } catch (err) {
      console.error('[AppSettings] Failed to save shortcuts:', err);
      localStorage.setItem(lsKey, JSON.stringify(shortcuts));
    }
  }, [authContext, shortcutCatalogue]);

  // ── App data ─────────────────────────────────────────────────────────
  const [showClearCache, setShowClearCache] = useState(false);
  const handleClearCache = useCallback(async () => {
    try {
      const keep = new Set(['theme']);
      Object.keys(localStorage)
        .filter((k) => !keep.has(k) && !k.startsWith('firebase:'))
        .forEach((k) => localStorage.removeItem(k));
      if ('caches' in window) {
        const names = await caches.keys();
        await Promise.all(names.map((n) => caches.delete(n)));
      }
      setToast(t('pages.AppSettings.cachedDataClearedReloading'));
      setTimeout(() => window.location.reload(), 800);
    } catch (err) {
      console.error('[AppSettings] Clear cache error:', err);
      setToast(t('pages.AppSettings.failedToClearCachedData'));
    }
  }, [t]);

  const pageContent = (
    <BasePageWithHeader
      ref={baseHeaderRef}
      title={t('pages.AppSettings.settings')}
      toolbarClassName={styles.toolbar}
      smallTitleClassName={styles.smallTitle}
      titleHeaderClassName={styles.titleHeader}
      startButtons={
        onClose ? (
          <IonButton shape="round" className={styles.closeButton} onClick={onClose}>
            <IonIcon icon={closeOutline} />
          </IonButton>
        ) : (
          <IonButton
            onClick={handleClose}
            shape="round"
            color="primary"
            aria-label={t('pages.AppSettings.back')}
            className={styles.backButton}
          >
            <IonIcon icon={chevronBack} />
          </IonButton>
        )
      }
      endButtons={
        <IonButton style={{ visibility: 'hidden' }}>
          <IonIcon icon={chevronBack} />
        </IonButton>
      }
    >
      <IonContent scrollY={false} className={styles.content}>
        <IonGrid
          ref={containerRef}
          className={styles.settingsContainer}
          onScroll={(e) => handleScroll((e.target as HTMLElement).scrollTop || 0)}
        >
          <div className={styles.titleCardWrapper}>
            <TitleCard
              ref={titleCardAnimationRef}
              title={t('pages.AppSettings.settings')}
              cardClassName={styles.settingsOverviewCard}
              titleContainerClassName={styles.titleContainer}
              titleClassName={styles.settingsTitle}
            />
          </div>

          {/* Appearance */}
          <IonCard className={styles.sectionCard}>
            <IonCardHeader>
              <IonCardTitle className={styles.sectionTitle}>
                <IonIcon icon={contrastOutline} className={styles.sectionIcon} />{t('pages.AppSettings.appearance')}
              </IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <IonList className={styles.settingsList}>
                <IonItem lines="none" className={`${styles.settingsItem} ${styles.themeItem}`}>
                  <div className={styles.themeWrapper}>
                    <div className={styles.themeHeader}>
                      <div className={`${styles.iconBadge} ${styles.iconBadgePrimary}`}>
                        <IonIcon icon={contrastOutline} />
                      </div>
                      <IonLabel>
                        <h3>{t('pages.AppSettings.theme')}</h3>
                        <p>{t('pages.AppSettings.chooseHowTheAppLooksOn')}</p>
                      </IonLabel>
                    </div>
                    <IonSegment
                      value={currentTheme}
                      onIonChange={(e) => applyTheme(e.detail.value as string)}
                      className={styles.themeSegment}
                    >
                      <IonSegmentButton value="light">
                        <IonIcon icon={sunnyOutline} />
                      </IonSegmentButton>
                      <IonSegmentButton value="dark">
                        <IonIcon icon={moonOutline} />
                      </IonSegmentButton>
                      <IonSegmentButton value="system">
                        <IonIcon icon={desktopOutline} />
                      </IonSegmentButton>
                    </IonSegment>
                  </div>
                </IonItem>
              </IonList>
            </IonCardContent>
          </IonCard>

          {/* Notifications */}
          <IonCard className={styles.sectionCard}>
            <IonCardHeader>
              <IonCardTitle className={styles.sectionTitle}>
                <IonIcon icon={notificationsOutline} className={styles.sectionIcon} />{t('pages.AppSettings.notifications')}
              </IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <IonList className={styles.settingsList}>
                <IonItem className={styles.settingsItem}>
                  <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgePrimary}`}>
                    <IonIcon icon={phonePortraitOutline} />
                  </div>
                  <IonLabel>
                    <h3>{t('pages.AppSettings.pushNotifications')}</h3>
                    <p>
                      {browserPermission === 'granted' && 'Alerts on this device'}
                      {browserPermission === 'denied' && 'Blocked — enable in browser settings'}
                      {browserPermission === 'default' && 'Permission required'}
                    </p>
                  </IonLabel>
                  {browserPermission === 'granted' ? (
                    <IonToggle
                      slot="end"
                      checked={pushEnabled}
                      onIonChange={(e) => { setPushEnabled(e.detail.checked); savePrefs({ push: e.detail.checked }); }}
                    />
                  ) : (
                    <IonBadge
                      slot="end"
                      className={styles.enableBadge}
                      role="button"
                      tabIndex={0}
                      onClick={handleRequestPermission}
                    >{t('pages.AppSettings.enable')}
                    </IonBadge>
                  )}
                </IonItem>
                <IonItem className={styles.settingsItem}>
                  <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgeSecondary}`}>
                    <IonIcon icon={mailOutline} />
                  </div>
                  <IonLabel>
                    <h3>{t('pages.AppSettings.emailNotifications')}</h3>
                    <p>{t('pages.AppSettings.receiveNotificationsViaEmail')}</p>
                  </IonLabel>
                  <IonToggle
                    slot="end"
                    checked={emailEnabled}
                    onIonChange={(e) => { setEmailEnabled(e.detail.checked); savePrefs({ email: e.detail.checked }); }}
                  />
                </IonItem>
                <IonItem className={styles.settingsItem}>
                  <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgeTertiary}`}>
                    <IonIcon icon={volumeHighOutline} />
                  </div>
                  <IonLabel>
                    <h3>{t('pages.AppSettings.sound')}</h3>
                    <p>{t('pages.AppSettings.playSoundForNotifications')}</p>
                  </IonLabel>
                  <IonToggle
                    slot="end"
                    checked={soundEnabled}
                    disabled={!pushEnabled}
                    onIonChange={(e) => { setSoundEnabled(e.detail.checked); savePrefs({ sound: e.detail.checked }); }}
                  />
                </IonItem>
                <IonItem
                  button
                  detail
                  detailIcon={chevronForwardOutline}
                  lines="none"
                  className={styles.settingsItem}
                  onClick={() => setShowNotificationsModal(true)}
                >
                  <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgeNeutral}`}>
                    <IonIcon icon={notificationsOutline} />
                  </div>
                  <IonLabel>
                    <h3>{t('pages.AppSettings.notificationCategories')}</h3>
                    <p>{t('pages.AppSettings.fineTuneWhichEventsNotifyYou')}</p>
                  </IonLabel>
                </IonItem>
              </IonList>
            </IonCardContent>
          </IonCard>

          {/* Security */}
          <IonCard className={styles.sectionCard}>
            <IonCardHeader>
              <IonCardTitle className={styles.sectionTitle}>
                <IonIcon icon={lockClosedOutline} className={styles.sectionIcon} />{t('pages.AppSettings.security')}
              </IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <IonList className={styles.settingsList}>
                <IonItem
                  button
                  detail
                  detailIcon={chevronForwardOutline}
                  lines="none"
                  className={styles.settingsItem}
                  onClick={() => setShowChangePassword(true)}
                >
                  <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgePrimary}`}>
                    <IonIcon icon={keyOutline} />
                  </div>
                  <IonLabel>
                    <h3>{t('pages.AppSettings.changePassword')}</h3>
                    <p>{t('pages.AppSettings.updateYourAccountPassword')}</p>
                  </IonLabel>
                </IonItem>
                <IonItem
                  button
                  detail
                  detailIcon={chevronForwardOutline}
                  lines="none"
                  className={styles.settingsItem}
                  onClick={() => setShowPasskeysModal(true)}
                >
                  <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgeSecondary}`}>
                    <IonIcon icon={fingerPrintOutline} />
                  </div>
                  <IonLabel>
                    <h3>{t('pages.AppSettings.passkeys')}</h3>
                    <p>{t('pages.AppSettings.passwordlessSignInWithBiometricsOr')}</p>
                  </IonLabel>
                  {passkeyCount !== null && (
                    <IonBadge slot="end" className={styles.countBadge} aria-label={`${passkeyCount} passkeys`}>
                      {passkeyCount}
                    </IonBadge>
                  )}
                </IonItem>
                <IonItem
                  button
                  detail
                  detailIcon={chevronForwardOutline}
                  lines="none"
                  className={styles.settingsItem}
                  onClick={() => setShowSecurityModal(true)}
                >
                  <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgeTertiary}`}>
                    <IonIcon icon={shieldCheckmarkOutline} />
                  </div>
                  <IonLabel>
                    <h3>{t('pages.AppSettings.securityActivity')}</h3>
                    <p>{t('pages.AppSettings.activeSessionsAndLoginHistory')}</p>
                  </IonLabel>
                </IonItem>
              </IonList>
            </IonCardContent>
          </IonCard>

          {/* App */}
          <IonCard className={styles.sectionCard}>
            <IonCardHeader>
              <IonCardTitle className={styles.sectionTitle}>
                <IonIcon icon={gridOutline} className={styles.sectionIcon} />{t('pages.AppSettings.app')}
              </IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <IonList className={styles.settingsList}>
                <LanguageSetting
                  itemClassName={styles.settingsItem}
                  badgeClassName={`${styles.iconBadge} ${styles.iconBadgeSecondary}`}
                />
                {shortcutCatalogue && (
                  <IonItem
                    button
                    detail
                    detailIcon={chevronForwardOutline}
                    lines="none"
                    className={styles.settingsItem}
                    onClick={() => setShowShortcutsModal(true)}
                  >
                    <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgePrimary}`}>
                      <IonIcon icon={gridOutline} />
                    </div>
                    <IonLabel>
                      <h3>{t('pages.AppSettings.dashboardShortcuts')}</h3>
                      <p>{t('pages.AppSettings.editTheQuickAccessButtonsOn')}</p>
                    </IonLabel>
                  </IonItem>
                )}
                <IonItem
                  button
                  detail
                  detailIcon={chevronForwardOutline}
                  lines="none"
                  className={styles.settingsItem}
                  onClick={() => setShowClearCache(true)}
                >
                  <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgeWarning}`}>
                    <IonIcon icon={trashBinOutline} />
                  </div>
                  <IonLabel>
                    <h3>{t('pages.AppSettings.clearCachedData')}</h3>
                    <p>{t('pages.AppSettings.freeUpSpaceAndReloadFresh')}</p>
                  </IonLabel>
                </IonItem>
                <IonItem lines="none" className={styles.settingsItem}>
                  <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgeNeutral}`}>
                    <IonIcon icon={informationCircleOutline} />
                  </div>
                  <IonLabel>
                    <h3>{t('pages.AppSettings.appVersion')}</h3>
                    <p>{APP_VERSION || t('pages.AppSettings.rSMaidsService')}</p>
                  </IonLabel>
                </IonItem>
              </IonList>
            </IonCardContent>
          </IonCard>

          <IonModal
            isOpen={showSecurityModal}
            onDidDismiss={() => setShowSecurityModal(false)}
            canDismiss={true}
            backdropDismiss={true}
            keepContentsMounted={false}
            handle={false}
          >
            <Security onClose={() => setShowSecurityModal(false)} />
          </IonModal>

          <IonModal
            isOpen={showPasskeysModal}
            onDidDismiss={() => setShowPasskeysModal(false)}
            onWillDismiss={() => setShowPasskeysModal(false)}
            canDismiss={true}
            backdropDismiss={true}
            keepContentsMounted={false}
            handle={false}
          >
            <PasskeyManagement
              onClose={() => setShowPasskeysModal(false)}
              onPasskeysChange={setPasskeyCount}
            />
          </IonModal>

          <IonModal
            isOpen={showNotificationsModal}
            onDidDismiss={() => setShowNotificationsModal(false)}
            canDismiss={true}
            backdropDismiss={true}
            keepContentsMounted={false}
            handle={false}
          >
            <NotificationSettings onClose={() => setShowNotificationsModal(false)} />
          </IonModal>

          {shortcutCatalogue && (
            <IonModal
              isOpen={showShortcutsModal}
              onDidDismiss={() => setShowShortcutsModal(false)}
              canDismiss={true}
              backdropDismiss={true}
              keepContentsMounted={false}
              handle={false}
            >
              <ShortcutsEditModal
                onDismiss={() => setShowShortcutsModal(false)}
                activeShortcutIds={activeShortcutIds}
                allShortcuts={shortcutCatalogue.shortcuts}
                categoryNames={shortcutCatalogue.categoryNames}
                onSave={saveShortcuts}
              />
            </IonModal>
          )}
        </IonGrid>
      </IonContent>

      <IonAlert
        isOpen={showChangePassword}
        cssClass="glass-alert"
        header={t('pages.AppSettings.changePassword')}
        inputs={[
          { name: 'currentPassword', type: 'password', placeholder: t('pages.AppSettings.currentPassword') },
          { name: 'newPassword', type: 'password', placeholder: t('pages.AppSettings.newPasswordMin6Characters') },
          { name: 'confirmPassword', type: 'password', placeholder: t('pages.AppSettings.confirmNewPassword') },
        ]}
        buttons={[
          { text: t('pages.AppSettings.cancel'), role: 'cancel' },
          { text: t('pages.AppSettings.update'), handler: (data) => { void handleChangePassword(data); } },
        ]}
        onDidDismiss={() => setShowChangePassword(false)}
      />

      <IonAlert
        isOpen={showClearCache}
        cssClass="glass-alert"
        header={t('pages.AppSettings.clearCachedData')}
        message={t('pages.AppSettings.thisClearsLocallyCachedDataAnd')}
        buttons={[
          { text: t('pages.AppSettings.cancel'), role: 'cancel', handler: () => setShowClearCache(false) },
          { text: t('pages.AppSettings.clear'), role: 'destructive', handler: handleClearCache },
        ]}
        onDidDismiss={() => setShowClearCache(false)}
      />

      <IonToast
        isOpen={!!toast}
        message={toast}
        duration={2500}
        onDidDismiss={() => setToast('')}
      />
    </BasePageWithHeader>
  );

  return onClose ? (
    <div className={styles.modalWrapper}>{pageContent}</div>
  ) : (
    <IonPage className={styles.page}>{pageContent}</IonPage>
  );
};

export default AppSettings;
